import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle, WarningCircle, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function POAccept() {
  const { token } = useParams();
  const [po, setPo] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(null);

  useEffect(() => {
    api
      .get(`/po/public/${token}`)
      .then((r) => {
        setPo(r.data);
        if (r.data.accepted_at) setAccepted(r.data.accepted_at);
      })
      .catch((err) => setError(apiError(err.response?.data?.detail) || "This link is invalid or has expired"))
      .finally(() => setLoading(false));
  }, [token]);

  const confirm = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post(`/po/public/${token}/accept`);
      setAccepted(data.accepted_at);
      toast.success(`${data.po_no} confirmed`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not confirm this purchase order");
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 p-6" data-testid="po-accept-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <img src="/dvepl-logo.svg" alt="DVEPL" className="w-9 h-9 rounded-md" />
          <span className="font-heading font-black text-lg text-zinc-50">DVEPL</span>
        </div>

        {loading && (
          <div className="text-center text-sm text-zinc-500 font-mono" data-testid="po-accept-loading">
            Loading purchase order…
          </div>
        )}

        {!loading && error && (
          <div className="border border-red-500/30 bg-red-500/5 rounded-sm p-8 text-center" data-testid="po-accept-error">
            <WarningCircle size={32} className="text-red-400 mx-auto mb-3" weight="fill" />
            <p className="text-sm text-zinc-300">{error}</p>
          </div>
        )}

        {!loading && !error && po && (
          <div className="border border-zinc-800 bg-zinc-900/50 rounded-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-2.5">
              <Receipt size={20} className="text-emerald-600" weight="bold" />
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-600 font-bold">
                  Purchase Order
                </div>
                <div className="font-heading text-xl font-black text-zinc-50 tracking-tight" data-testid="po-accept-no">
                  {po.po_no}
                </div>
              </div>
            </div>

            <div className="px-5 py-4 space-y-1.5 text-sm">
              <div className="flex justify-between text-zinc-400">
                <span>Vendor</span>
                <span className="text-zinc-100">{po.vendor_name}</span>
              </div>
              <div className="flex justify-between text-zinc-400">
                <span>Date</span>
                <span className="text-zinc-100 font-mono">{po.po_date}</span>
              </div>
              <div className="flex justify-between text-zinc-400">
                <span>From</span>
                <span className="text-zinc-100">{po.company_name}</span>
              </div>
            </div>

            <div className="px-5 pb-3">
              <div className="border border-zinc-800 rounded-sm overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-zinc-950 text-zinc-500 uppercase text-[9px] font-mono tracking-wider">
                      <th className="text-left px-2.5 py-2 font-bold">Item</th>
                      <th className="text-right px-2.5 py-2 font-bold">Qty</th>
                      <th className="text-right px-2.5 py-2 font-bold">Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {(po.items || []).map((it, i) => (
                      <tr key={i} data-testid={`po-accept-item-${i}`}>
                        <td className="px-2.5 py-2 text-zinc-200">
                          {it.item}
                          {it.cat_no && <span className="text-zinc-500"> ({it.cat_no})</span>}
                        </td>
                        <td className="px-2.5 py-2 text-right font-mono text-zinc-300">{it.qty}</td>
                        <td className="px-2.5 py-2 text-right font-mono text-zinc-300">₹ {inr(it.rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="px-5 py-3 flex justify-between items-center border-t border-zinc-800 bg-zinc-950/60">
              <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Grand Total</span>
              <span className="font-mono text-lg font-bold text-emerald-600" data-testid="po-accept-total">
                ₹ {inr(po.grand_total)}
              </span>
            </div>

            <div className="px-5 py-5 border-t border-zinc-800">
              {accepted ? (
                <div className="text-center" data-testid="po-accept-success">
                  <CheckCircle size={32} className="text-emerald-400 mx-auto mb-2" weight="fill" />
                  <p className="text-sm text-zinc-200 font-semibold">Confirmed — thank you.</p>
                  <p className="text-xs text-zinc-500 mt-1">
                    Recorded on {String(accepted).slice(0, 16).replace("T", " ")}. {po.company_name} has been notified.
                  </p>
                </div>
              ) : po.status === "cancelled" ? (
                <div className="text-center text-sm text-red-400" data-testid="po-accept-cancelled">
                  This purchase order has been cancelled and no longer requires confirmation.
                </div>
              ) : (
                <>
                  <Button
                    onClick={confirm}
                    disabled={accepting}
                    data-testid="po-accept-confirm-btn"
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-semibold h-11 gap-1.5"
                  >
                    <CheckCircle size={17} weight="bold" /> {accepting ? "Confirming…" : "Confirm & Accept this PO"}
                  </Button>
                  <p className="text-[11px] text-zinc-500 mt-2.5 text-center">
                    By confirming, you acknowledge receipt of this purchase order and its terms.
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
